import type { Slide, TableCell, TextProps } from '@features/powerpoint';
import type { ValueInput } from '@utils/formattersV2';
import { formatters } from '@utils/formattersV2';
import type {
  ChannelGrouping,
  Lci3SalesMetrics,
} from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { themeV2 } from '../../../domain/theme-v2';
import { DEFAULT_FONT } from '../../../utils/constants-v2';
import { generateTableV2 } from '../../../utils/build-table-v2';
import { contentSlideHeaderV2 } from '../../../utils/slide-header-v2';
import {
  splitKeyTakeaways,
  parsedTakeawaysV2,
} from '../../../domain/key-takeaways-v2';
import { keyTakeawaysSparkle } from '../../../images';
import {
  SUMMARY_TABLE_ROW_KEYS,
  type SummaryTableRowKey,
} from '../sales-lift-summary-table-slide';

type SalesLiftSummaryTableSlideV2Props = {
  slideMaster: string;
  overallSalesMetrics: Lci3SalesMetrics;
  excludedRows?: SummaryTableRowKey[];
  keyTakeaways: string;
  rsaOverallResults: string;
};

const TABLE_HEADER_OPTIONS: TableCell['options'] = {
  color: themeV2.colors.metricBlue,
  bold: true,
  fontFace: DEFAULT_FONT,
  fontSize: 11,
};

const summaryTableColumnLabels: TableCell[] = [
  { text: '', options: TABLE_HEADER_OPTIONS },
  { text: 'Combined', options: TABLE_HEADER_OPTIONS },
  { text: 'In-Store', options: TABLE_HEADER_OPTIONS },
  { text: 'Online', options: TABLE_HEADER_OPTIONS },
];

const getLiftRowFromChannelGrouping = (
  label: string,
  grouping: ChannelGrouping,
  lift: ChannelGrouping,
  formatter: (value: ValueInput, lift: ValueInput) => string
): TableCell[] => [
  { text: label },
  { text: formatter(grouping.total, lift.total) },
  { text: formatter(grouping.inStore, lift.inStore) },
  { text: formatter(grouping.online, lift.online) },
];

const getRowFromChannelGrouping = (
  label: string,
  grouping: ChannelGrouping,
  formatter: (value: ValueInput) => string
): TableCell[] => [
  { text: label },
  { text: formatter(grouping.total) },
  { text: formatter(grouping.inStore) },
  { text: formatter(grouping.online) },
];

const subdimensionRows = (
  overallSalesMetrics: Lci3SalesMetrics,
  excludedRows: SummaryTableRowKey[] = []
): TableCell[][] => {
  const rows: Record<SummaryTableRowKey, TableCell[]> = {
    incrementalSalesLift: getRowFromChannelGrouping(
      'Incremental Sales Lift',
      overallSalesMetrics.incrementalSalesLift,
      value => formatters.lift(value)
    ),
    confidenceLevel: getLiftRowFromChannelGrouping(
      'Confidence Level',
      overallSalesMetrics.confidenceLevel,
      overallSalesMetrics.incrementalSalesLift,
      (value, lift) => formatters.confidenceLevel(value, lift)
    ),
    incrementalSales: getRowFromChannelGrouping(
      'Incremental Sales',
      overallSalesMetrics.incrementalSales,
      value => formatters.currency(value, { full: true, decimals: 0 })
    ),
    totalProjectedSales: getRowFromChannelGrouping(
      'Total Sales',
      overallSalesMetrics.totalProjectedSales,
      value => formatters.currency(value, { full: true, decimals: 0 })
    ),
    exposedSalesRate: getRowFromChannelGrouping(
      'Exposed Sales Rate',
      overallSalesMetrics.exposedSalesRate,
      value => formatters.currency(value, { full: true, decimals: 2 })
    ),
    controlSalesRate: getRowFromChannelGrouping(
      'Control Sales Rate',
      overallSalesMetrics.controlSalesRate,
      value => formatters.currency(value, { full: true, decimals: 2 })
    ),
    exposedTransactionSize: getRowFromChannelGrouping(
      'Exposed Transaction Size',
      overallSalesMetrics.exposedTransactionSize,
      value => formatters.currency(value)
    ),
    controlTransactionSize: getRowFromChannelGrouping(
      'Control Transaction Size',
      overallSalesMetrics.controlTransactionSize,
      value => formatters.currency(value)
    ),
    transactionSizeLift: getRowFromChannelGrouping(
      'Transaction Size Lift',
      overallSalesMetrics.transactionSizeLift,
      value => formatters.percentage(value)
    ),
    exposedTransactionRate: getRowFromChannelGrouping(
      'Exposed Transaction Rate',
      overallSalesMetrics.exposedTransactionRate,
      value => formatters.number(value, { decimals: 1 })
    ),
    controlTransactionRate: getRowFromChannelGrouping(
      'Control Transaction Rate',
      overallSalesMetrics.controlTransactionRate,
      value => formatters.number(value, { decimals: 1 })
    ),
    transactionRateLift: getRowFromChannelGrouping(
      'Transaction Rate Lift',
      overallSalesMetrics.transactionRateLift,
      value => formatters.percentage(value, { decimals: 1 })
    ),
    exposedPenetration: getRowFromChannelGrouping(
      'Exposed Penetration',
      overallSalesMetrics.exposedPenetration,
      value => formatters.percentage(value, { decimals: 1 })
    ),
    controlPenetration: getRowFromChannelGrouping(
      'Control Penetration',
      overallSalesMetrics.controlPenetration,
      value => formatters.percentage(value, { decimals: 1 })
    ),
    penetrationLift: getRowFromChannelGrouping(
      'Penetration Lift',
      overallSalesMetrics.penetrationLift,
      value => formatters.percentage(value, { decimals: 1 })
    ),
  };

  return SUMMARY_TABLE_ROW_KEYS.filter(key => !excludedRows.includes(key)).map(
    key => rows[key]
  );
};

const keyTakeawaysSection = (
  keyTakeaways: string,
  rsaOverallResults: string
): { texts: TextProps[]; images: Slide['images']; shapes: Slide['shapes'] } => {
  const texts: TextProps[] = [];
  const hasOverallResults = rsaOverallResults.trim().length > 0;
  const hasKeyTakeaways = keyTakeaways.trim().length > 0;

  if (hasOverallResults) {
    texts.push({
      text: parsedTakeawaysV2(splitKeyTakeaways(rsaOverallResults)),
      options: {
        h: 3.28,
        w: 4.5,
        x: 8.2,
        y: 1.64,
        valign: 'top',
        paraSpaceAfter: 12,
        fontSize: 12,
        fontFace: DEFAULT_FONT,
        color: themeV2.colors.metricBlue,
      },
    });
  }

  if (hasKeyTakeaways) {
    const keyTakeawaysY = hasOverallResults ? 4.55 : 1.64;

    texts.push(
      {
        text: 'Key Takeaways',
        options: {
          fontFace: DEFAULT_FONT,
          fontSize: 18,
          bold: true,
          color: themeV2.colors.black,
          x: 8.7,
          y: keyTakeawaysY,
          w: 4,
          h: 0.47,
        },
      },
      {
        text: parsedTakeawaysV2(splitKeyTakeaways(keyTakeaways)),
        options: {
          h: hasOverallResults ? 2 : 4.5,
          w: 4.5,
          x: 8.2,
          y: keyTakeawaysY + 0.7,
          valign: 'top',
          paraSpaceAfter: 12,
          fontSize: 12,
          fontFace: DEFAULT_FONT,
          color: themeV2.colors.metricBlue,
        },
      }
    );

    return {
      texts,
      images: [
        {
          path: keyTakeawaysSparkle,
          x: 8.2,
          y: keyTakeawaysY + 0.03,
          h: 0.4,
          w: 0.4,
        },
      ],
      shapes: [
        {
          shapeName: 'line' as const,
          options: {
            h: 0,
            w: 4,
            x: 8.7,
            y: keyTakeawaysY + 0.5,
            line: {
              color: themeV2.colors.inmarketGreen,
              width: 1,
            },
          },
        },
      ],
    };
  }

  return { texts, images: [], shapes: [] };
};

export const salesLiftSummaryTableSlideV2 = (
  props: SalesLiftSummaryTableSlideV2Props
): Slide => {
  const hasCustomText =
    props.keyTakeaways.trim().length > 0 ||
    props.rsaOverallResults.trim().length > 0;

  const tableWidth = hasCustomText ? 7.5 : 12.35;

  const customContent = hasCustomText
    ? keyTakeawaysSection(props.keyTakeaways, props.rsaOverallResults)
    : { texts: [], images: [], shapes: [] };

  return {
    slideMaster: props.slideMaster,
    tables: [
      generateTableV2(
        summaryTableColumnLabels,
        subdimensionRows(props.overallSalesMetrics, props.excludedRows),
        { w: tableWidth, x: 0.33, y: 1.64, rowH: 0.3, fontSize: 10.5 }
      ),
    ],
    texts: [
      contentSlideHeaderV2('Summary Table - Sales Lift'),
      ...customContent.texts,
    ],
    images: customContent.images,
    shapes: customContent.shapes,
  };
};
